import {useMutation} from '@tanstack/react-query'
import {useFormik} from 'formik'
import {FC, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useHistory, useLocation} from 'react-router-dom'
import {FormLabel, FormPassword, GButton} from 'src/app/components/Libs'
import {PasswordFormUnique} from 'src/app/components/Widgets/PasswordFormUnique'
import {useHeaderToast} from 'src/app/context/ToastContext'
import {useErrorQuery} from 'src/app/hooks/error-query.hook'
import {useOnline} from 'src/app/hooks/online.hook'
import {useWebTitle} from 'src/app/hooks/title.hook'
import {resetPasswordUser, verificationUserToken} from 'src/app/services'
import NotifAuthRedux from 'src/app/store/Auth/NotifAuthRedux'
import {toAbsoluteUrl} from 'src/app/utils/asset-helpers.utils'
import {passwordRegExp} from 'src/app/utils/input.utils'
import * as Yup from 'yup'
import AuthScreens from '../../AuthScreens'

const resetSchema = Yup.object().shape({
  password: Yup.string()
    .max(256, 'Maksimal karakter 256')
    .matches(passwordRegExp, 'Pastikan kata sandi sesuai ketentuan')
    .required('Kata sandi baru wajib diisi'),
  new_password: Yup.string()
    .test('equal', 'Kata sandi tidak sesuai', function (v) {
      // Don't use arrow functions
      const ref = Yup.ref('password')
      return v === this.resolve(ref)
    })
    .max(256, 'Maksimal karakter 256')
    .required('Ulangi kata sandi wajib diisi'),
})

const initialValues = {
  password: '',
  new_password: '',
}

export const CreateNewPassword: FC = () => {
  const history = useHistory()
  let query = new URLSearchParams(useLocation().search)
  const token = query.get('t') as string
  useWebTitle('Buat Kata Sandi Baru')
  const {errorTemp} = useErrorQuery()
  const {addPageToasts} = useHeaderToast()
  const {isOnline, errorOffline} = useOnline()
  const dispatch = useDispatch()

  const [generatedToken, setGeneratedToken] = useState<string>('')
  const [isExpiredToken, setIsExpiredToken] = useState(false)

  const submitFn = useMutation({
    mutationFn: (payload: typeof initialValues) => {
      const val = {
        password: payload.new_password,
      }

      return resetPasswordUser(val, {Authorization: `Bearer ${generatedToken}`})
    },
    onSuccess: () => {
      const message = 'Kata sandi berhasil diubah'

      dispatch(NotifAuthRedux.actions.setNotification(message))
      history.push('/auth/login-email')
    },
    onError: (e) => errorTemp(e),
  })

  const formik = useFormik({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: resetSchema,
    onSubmit: (values) => {
      if (isOnline) {
        submitFn.mutate(values)
      } else {
        addPageToasts({scheme: 'danger', text: errorOffline})
      }
    },
  })

  useEffect(() => {
    const payload = {
      token: token,
    }

    if (token) {
      verificationUserToken(payload)
        .then((res) => {
          setIsExpiredToken(false)

          const data = res?.data?.response_output?.detail?.token
          setGeneratedToken(data)
        })
        .catch((e) => {
          setIsExpiredToken(true)
        })
    }
  }, [token, history])

  useEffect(() => {
    if (!token) history.replace(AuthScreens.LOGIN_EMAIL.PATH)
  }, [history, token])

  if (!token) return null

  return (
    <div data-testid='create-new-password-page' className='w-full'>
      {isExpiredToken ? (
        <>
          <div className='flex justify-center mb-8'>
            <img
              src={toAbsoluteUrl('/media/image/image_expired_link_2.svg')}
              alt='optera_logo'
              className='text-center'
            />
          </div>

          <div className='mb-6 text-center'>
            <div className='mb-2 font-semibold text-fs-5 text-neutral-900'>
              Tautan telah Kedaluwarsa
            </div>
            <div className='text-neutral-600 text-fs-7'>
              Silakan minta tautan baru untuk melanjutkan proses Lupa Kata Sandi.
            </div>
          </div>

          <GButton
            size='large'
            className='w-full'
            onClick={() => history.push(AuthScreens.LOGIN_EMAIL.PATH)}
          >
            Ke Halaman Login
          </GButton>
        </>
      ) : (
        <>
          <img
            src={toAbsoluteUrl('/media/logo/logo_optera.svg')}
            alt='optera_logo'
            className='h-[62px] mb-8'
          />

          <div className='mb-6'>
            <div className='mb-2 font-semibold text-fs-5 text-neutral-900'>Buat Kata Sandi</div>
            <div className='text-neutral-600 text-fs-7'>Masukkan Kata Sandi baru Anda</div>
          </div>

          <form className='w-full' onSubmit={formik.handleSubmit} noValidate>
            <div className='mb-8'>
              <FormLabel className='mb-2' textClassName='font-normal text-neutral-700' required>
                Kata Sandi
              </FormLabel>
              <PasswordFormUnique
                {...formik.getFieldProps('password')}
                name='password'
                placeholder='Masukkan kata sandi'
                maxLength={256}
                error={formik.errors.password}
                touched={formik.touched.password}
              />
            </div>

            <div className='mb-8'>
              <FormLabel className='mb-2' textClassName='font-normal text-neutral-700' required>
                Ulangi Kata Sandi
              </FormLabel>
              <FormPassword
                {...formik.getFieldProps('new_password')}
                name='new_password'
                placeholder='Masukkan ulang kata sandi'
                maxLength={256}
                error={formik.errors.new_password}
                touched={formik.touched.new_password}
              />
            </div>

            <GButton type='submit' size='large' className='w-full' loading={submitFn.isLoading}>
              Lanjut
            </GButton>
          </form>
        </>
      )}
    </div>
  )
}
